import { Icon } from '@advanza/ui'
import NiceModal from '@ebay/nice-modal-react'
import ChurnReasonEditor from 'components/cancellations/ChurnReasonEditor'
import TableList from 'components/TableList'
import { useAppSelector } from 'hooks/hooks'
import { useList } from 'hooks/listHooks'
import { hasAccess, isSuperUser } from 'misc/user'
import React from 'react'
import { Link } from 'react-router-dom'
import cancellationsSchema from 'schemes/cancellationsSchema'

interface Cancellation {
    premium_cancel_request_id: number
    service_provider_id: number
    reason: number | null
    reasonText?: string
    businessName?: string
    explanation?: string
    [key: string]: unknown
}

interface CancellationsListState {
    entities: {
        cancellations: Record<number, Cancellation>
    }
    extra: {
        definitionsCancellations: Record<string, { name: string }>
    }
    refresh: (forced?: boolean) => void
    [key: string]: unknown
}

interface CancellationsListProps {
    filterId?: string
}

export function useCancellationsList(
    filterId: string,
    defaultOptions?: Record<string, unknown>,
): CancellationsListState {
    const { countries } = useAppSelector((state) => state.countries)
    return useList(
        filterId,
        {
            url: 'office/cancellations/get-list',
            schema: cancellationsSchema,
            reducerKey: 'cancellations',
        },
        { ...defaultOptions, countries, sorting: ['premium_cancel_request_id', 'DESC'] },
    ) as CancellationsListState
}

const CancellationsList = ({ filterId = 'all' }: CancellationsListProps) => {
    const {
        entities: { cancellations = {} } = {},
        extra: { definitionsCancellations = {} } = {},
        refresh,
        ...list
    } = useCancellationsList(filterId)
    const isAdmin = isSuperUser() || hasAccess('providers', 'w')

    const handleEditReason = (cancellation: Cancellation) => {
        NiceModal.show(ChurnReasonEditor, {
            premiumCancelRequestId: cancellation.premium_cancel_request_id,
            currentReason: cancellation.reason,
            providerId: cancellation.service_provider_id,
            onSave: () => {
                refresh(true)
            },
        })
    }

    const renderRow = (premiumCancelRequestId: number) => {
        const cancellation = cancellations[premiumCancelRequestId] || ({} as Cancellation)
        return {
            cols: Object.keys(definitionsCancellations).map((key) => {
                if (key === 'businessName') {
                    return (
                        <Link
                            key={key}
                            title="To pro"
                            className="plain"
                            to={`/service/providers/provider/${cancellation.service_provider_id}`}>
                            {cancellation.businessName}
                        </Link>
                    )
                } else if (key === 'explanation') {
                    return (
                        <span
                            key={key}
                            className="break-words overflow-hidden max-w-[500px] inline-block">
                            {cancellation.explanation}
                        </span>
                    )
                } else if (key === 'reasonText') {
                    const reasonContent = cancellation.reasonText || cancellation[key]
                    // Show icon if admin - check both superuser and providers write permission
                    if (isAdmin) {
                        return (
                            <span key={key} className="inline-flex items-center gap-2">
                                <span>{reasonContent}</span>
                                <Icon
                                    name="edit"
                                    className="cursor-pointer text-xl text-blue-500 shrink-0"
                                    onClick={(e: React.MouseEvent) => {
                                        e.stopPropagation()
                                        handleEditReason(cancellation)
                                    }}
                                    title="Edit churn reason"
                                />
                            </span>
                        )
                    }
                    return <React.Fragment key={key}>{reasonContent}</React.Fragment>
                }

                return <React.Fragment key={key}>{String(cancellation[key] ?? '')}</React.Fragment>
            }),
        }
    }

    return (
        <TableList
            definitions={definitionsCancellations}
            renderRow={renderRow}
            addListFeatures
            {...list}
        />
    )
}

export default CancellationsList
